import React from 'react';
import { useLanguage, languages } from '@/context/LanguageContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Globe, Check, Sparkles } from 'lucide-react';

const LanguageSelector = ({ variant = 'default' }) => {
  const { language, setLanguage, detectedLanguage } = useLanguage();
  
  const currentLang = languages.find(l => l.code === language) || languages[0];
  const detectedLang = languages.find(l => l.code === detectedLanguage);
  
  // Show detected hint only if user hasn't selected their own language yet
  // and detected is different from current
  const showDetectedHint = detectedLanguage !== language && detectedLang;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="ghost" 
          size={variant === 'compact' ? 'sm' : 'default'}
          className="flex items-center gap-2"
          data-testid="language-selector"
        >
          <Globe className="w-4 h-4" />
          <span className="hidden sm:inline">{currentLang.flag}</span>
          <span className="hidden md:inline text-sm">{currentLang.name}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52">
        {showDetectedHint && (
          <>
            <DropdownMenuLabel className="text-xs text-muted-foreground flex items-center gap-1">
              <Sparkles className="w-3 h-3" />
              Detected from browser
            </DropdownMenuLabel>
            <DropdownMenuItem
              onClick={() => setLanguage(detectedLanguage)}
              className="flex items-center justify-between cursor-pointer bg-accent/50"
              data-testid={`lang-detected-${detectedLanguage}`}
            >
              <div className="flex items-center gap-2">
                <span className="text-lg">{detectedLang.flag}</span>
                <span>{detectedLang.name}</span>
              </div>
              <span className="text-xs text-muted-foreground">Auto</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        )}
        {languages.map((lang) => (
          <DropdownMenuItem
            key={lang.code}
            onClick={() => setLanguage(lang.code)}
            className="flex items-center justify-between cursor-pointer"
            data-testid={`lang-${lang.code}`}
          >
            <div className="flex items-center gap-2">
              <span className="text-lg">{lang.flag}</span>
              <span>{lang.name}</span>
            </div>
            {language === lang.code && (
              <Check className="w-4 h-4 text-foreground" />
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default LanguageSelector;
